import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Save as SaveIcon,
  CheckCircle as ApproveIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import {
  COACHES,
  getCoachByUid,
  getCoachRegistrations,
  getCoachInvites,
  deleteCoachInvite,
  sendCoachInviteLinkEmail,
  sendCoachConfirmationEmail,
  updateCoachRegistration,
  addApprovedCoach,
  getApprovedCoachUids,
  getCoachProfile,
  updateCoachProfile,
  getUserProfile,
  updateUserProfile,
  addCoachToFirestoreList,
  seedCoachesIfEmpty,
  PRICING,
  createMailDocument,
  coachHasUpcomingBookableSlots
} from '../utils/firebaseUtils';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';

const AdminTreneriaiPage = () => {
  const { currentUser, refreshApprovedCoaches } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [approvedUids, setApprovedUids] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [coachProfiles, setCoachProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [coachInvites, setCoachInvites] = useState([]);
  const [sendingInviteId, setSendingInviteId] = useState(null);
  const [deletingInviteId, setDeletingInviteId] = useState(null);
  const [inviteSentOpen, setInviteSentOpen] = useState(false);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [emailSelectedUids, setEmailSelectedUids] = useState(new Set());
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccessOpen, setEmailSuccessOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [coachActiveStatus, setCoachActiveStatus] = useState({}); // uid -> true (has bookable slots) | false (not shown on user side)
  const emailBodyInputRef = useRef(null);

  const handleEmailBodyKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const tab = '\t';
      const newValue = emailBody.slice(0, start) + tab + emailBody.slice(end);
      setEmailBody(newValue);
      const newPos = start + tab.length;
      setTimeout(() => {
        if (emailBodyInputRef.current) {
          emailBodyInputRef.current.selectionStart = newPos;
          emailBodyInputRef.current.selectionEnd = newPos;
          emailBodyInputRef.current.focus();
        }
      }, 0);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      seedCoachesIfEmpty().catch(() => {}); // One-time: populate Firestore coaches list if empty (for web + mobile)
      const [regList, uids, invites] = await Promise.all([
        getCoachRegistrations(),
        getApprovedCoachUids(),
        getCoachInvites()
      ]);
      setRegistrations(regList);
      setApprovedUids(uids);
      setCoachInvites(invites);

      const userProfMap = {};
      const coachProfMap = {};
      const hardcodedUids = COACHES.map((c) => c.uid);
      const uidsToFetch = [...new Set([...regList.map((r) => r.userId).filter(Boolean), ...uids, ...hardcodedUids])];
      for (const uid of uidsToFetch) {
        try {
          const coach = getCoachByUid(uid);
          // Hardcoded coaches store profile under coach id (e.g. juozasp); approved-only under uid
          const coachProfileId = coach ? coach.id : uid;
          const [up, cp] = await Promise.all([
            getUserProfile(uid).catch(() => null),
            getCoachProfile(coachProfileId).catch(() => null)
          ]);
          if (up) userProfMap[uid] = up;
          if (cp) coachProfMap[uid] = cp;
        } catch (_) {}
      }
      setUserProfiles(userProfMap);
      setCoachProfiles(coachProfMap);
    } catch (err) {
      console.error(err);
      setError('Nepavyko įkelti duomenų');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load which coaches have upcoming bookable slots (active = shown on user side)
  useEffect(() => {
    const uids = [...new Set([...COACHES.map((c) => c.uid), ...approvedUids])];
    if (uids.length === 0) return;
    let cancelled = false;
    Promise.all(
      uids.map(async (uid) => {
        const coach = getCoachByUid(uid);
        const coachId = coach?.id ?? uid;
        const has = await coachHasUpcomingBookableSlots(coachId);
        return { uid, has };
      })
    ).then((results) => {
      if (cancelled) return;
      setCoachActiveStatus((prev) => {
        const next = { ...prev };
        results.forEach(({ uid, has }) => { next[uid] = has; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [approvedUids.join(','), registrations.length]);

  const isApproved = (userId) => approvedUids.includes(userId);
  const getProfile = (userId) => userProfiles[userId] || null;
  const getCoachProf = (userId) => coachProfiles[userId] || null;

  const allActiveUids = [...new Set([...COACHES.map((c) => c.uid), ...approvedUids])];
  const activeCoaches = allActiveUids.map((uid) => {
    const coach = getCoachByUid(uid);
    const reg = registrations.find((r) => r.userId === uid);
    const prof = getProfile(uid);
    const cprof = getCoachProf(uid);
    // Prefer coachProfile (cprof) for contact so saved admin edits show; COACHES has empty for some (e.g. Juozas)
    return {
      uid,
      reg,
      name: coach?.name ?? reg?.fullName ?? prof?.fullName ?? cprof?.fullName ?? '—',
      email: cprof?.email ?? coach?.email ?? reg?.email ?? prof?.email ?? '—',
      phone: cprof?.phone ?? coach?.phone ?? reg?.phoneNumber ?? prof?.phoneNumber ?? '—',
      cprof,
      prof
    };
  });

  // Active (has bookable slots) first, then not active; same order within each group
  const sortedActiveCoaches = [...activeCoaches].sort((a, b) => {
    const aActive = coachActiveStatus[a.uid] === true;
    const bActive = coachActiveStatus[b.uid] === true;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return 0;
  });

  const pendingRegistrations = registrations.filter((r) => !isApproved(r.userId));

  const startEdit = (item) => {
    const uid = item.userId ?? item.uid;
    const prof = getProfile(uid);
    const cprof = getCoachProf(uid);
    const reg = item.reg ?? item;
    const price1 = reg.price1Person ?? cprof?.price1Person ?? PRICING.ONE_PERSON_PER_HOUR;
    const price2 = reg.price2People ?? cprof?.price2People ?? PRICING.TWO_PEOPLE_PER_HOUR;
    const price3 = reg.price3People ?? cprof?.price3People ?? PRICING.THREE_FOUR_PERSONS_PER_HOUR;
    const price4 = reg.price4People ?? cprof?.price4People ?? PRICING.THREE_FOUR_PERSONS_PER_HOUR;
    const expandKey = item.reg?.id ?? `active-${uid}`;
    setEditing({
      docId: reg.id ?? `active-${uid}`,
      userId: uid,
      fullName: item.name ?? reg.fullName ?? prof?.fullName ?? '',
      email: item.email ?? reg.email ?? prof?.email ?? '',
      phoneNumber: item.phone ?? reg.phoneNumber ?? prof?.phoneNumber ?? '',
      description: cprof?.description ?? '',
      price1Person: price1,
      price2People: price2,
      price3People: price3,
      price4People: price4
    });
    setExpandedId(expandKey);
  };

  const handleSave = async () => {
    if (!editing.userId) return;
    setSavingId(editing.userId);
    setError('');
    try {
      const isActiveOnly = editing.docId?.startsWith?.('active-');
      if (!isActiveOnly && editing.docId) {
        await updateCoachRegistration(editing.docId, {
          fullName: editing.fullName.trim(),
          email: editing.email.trim(),
          phoneNumber: editing.phoneNumber.trim()
        });
      }
      try {
        await updateUserProfile(editing.userId, {
          fullName: editing.fullName.trim(),
          email: editing.email.trim(),
          phoneNumber: editing.phoneNumber.trim()
        });
      } catch (err) {
        if (err.message !== 'User profile not found') throw err;
      }
      const coachForSave = getCoachByUid(editing.userId);
      const coachProfileId = coachForSave ? coachForSave.id : editing.userId;
      await updateCoachProfile(coachProfileId, {
        description: editing.description.trim(),
        email: editing.email.trim(),
        phone: editing.phoneNumber.trim(),
        price1Person: Number(editing.price1Person) || PRICING.ONE_PERSON_PER_HOUR,
        price2People: Number(editing.price2People) || PRICING.TWO_PEOPLE_PER_HOUR,
        price3People: Number(editing.price3People) || PRICING.THREE_FOUR_PERSONS_PER_HOUR,
        price4People: Number(editing.price4People) || PRICING.THREE_FOUR_PERSONS_PER_HOUR
      });
      await loadData();
      setEditing({});
      setExpandedId(null);
      setSaveSuccessOpen(true);
    } catch (err) {
      console.error(err);
      const msg = err?.message || err?.code || 'Nepavyko išsaugoti';
      setError(msg.includes('permission') || err?.code === 'permission-denied' ? 'Teisės nepakanka (tik administratorius gali redaguoti). ' + msg : msg);
    } finally {
      setSavingId(null);
    }
  };

  const handleApprove = async (reg) => {
    setApprovingId(reg.userId);
    setError('');
    try {
      await addApprovedCoach(reg.userId);
      await updateCoachRegistration(reg.id, { status: 'approved' });
      const uids = await getApprovedCoachUids();
      setApprovedUids(uids);
      await refreshApprovedCoaches();
      const prof = await getUserProfile(reg.userId).catch(() => null);
      const email = reg.email || prof?.email;
      if (email) {
        await sendCoachConfirmationEmail(email, reg.fullName || prof?.fullName);
      }
      // Sync registration contact and pricing into coachProfile so coaches display has them
      const price1 = reg.price1Person != null && reg.price1Person !== '' ? Number(reg.price1Person) : PRICING.ONE_PERSON_PER_HOUR;
      const price2 = reg.price2People != null && reg.price2People !== '' ? Number(reg.price2People) : PRICING.TWO_PEOPLE_PER_HOUR;
      const price3 = reg.price3People != null && reg.price3People !== '' ? Number(reg.price3People) : PRICING.THREE_FOUR_PERSONS_PER_HOUR;
      const price4 = reg.price4People != null && reg.price4People !== '' ? Number(reg.price4People) : PRICING.THREE_FOUR_PERSONS_PER_HOUR;
      await updateCoachProfile(reg.userId, {
        email: (reg.email || prof?.email || '').trim(),
        phone: (reg.phoneNumber || prof?.phoneNumber || '').trim(),
        price1Person: price1,
        price2People: price2,
        price3People: price3,
        price4People: price4
      });
      await addCoachToFirestoreList(reg.userId, {
        fullName: reg.fullName || prof?.fullName,
        email: reg.email || prof?.email,
        phoneNumber: reg.phoneNumber || prof?.phoneNumber
      });
    } catch (err) {
      console.error(err);
      setError('Nepavyko patvirtinti');
    } finally {
      setApprovingId(null);
    }
  };

  const handleSendInviteLink = async (invite) => {
    setSendingInviteId(invite.id);
    setError('');
    try {
      await sendCoachInviteLinkEmail(invite);
      setInviteSentOpen(true);
    } catch (err) {
      console.error(err);
      setError('Nepavyko išsiųsti nuorodos: ' + (err.message || 'Klaida'));
    } finally {
      setSendingInviteId(null);
    }
  };

  const handleDeleteInvite = async (invite) => {
    if (!window.confirm(`Ar tikrai norite pašalinti „${[invite.firstName, invite.lastName].filter(Boolean).join(' ') || invite.email}“ įrašą?`)) return;
    setDeletingInviteId(invite.id);
    setError('');
    try {
      await deleteCoachInvite(invite.id);
      setCoachInvites((prev) => prev.filter((i) => i.id !== invite.id));
    } catch (err) {
      console.error(err);
      setError('Nepavyko pašalinti: ' + (err.message || 'Klaida'));
    } finally {
      setDeletingInviteId(null);
    }
  };

  const toggleEmailSelect = (uid) => {
    setEmailSelectedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const selectAllCoachesForEmail = () => {
    setEmailSelectedUids(new Set(activeCoaches.map((c) => c.uid)));
  };

  const deselectAllCoachesForEmail = () => {
    setEmailSelectedUids(new Set());
  };

  const openEmailDialog = () => {
    setEmailSubject('');
    setEmailBody('');
    setEmailError('');
    setEmailDialogOpen(true);
  };

  const closeEmailDialog = () => {
    if (!emailSending) setEmailDialogOpen(false);
  };

  const handleSendCoachEmails = async () => {
    const toSend = activeCoaches.filter((c) => emailSelectedUids.has(c.uid) && (c.email || '').trim().length > 0);
    if (toSend.length === 0) {
      setEmailError('Pasirinkite bent vieną trenerį su el. paštu.');
      return;
    }
    const subj = (emailSubject || '').trim();
    const text = (emailBody || '').trim();
    if (!subj || !text) {
      setEmailError('Įveskite temą ir žinutę.');
      return;
    }
    setEmailSending(true);
    setEmailError('');
    try {
      for (const coach of toSend) {
        await createMailDocument(
          coach.email.trim(),
          { subject: subj, text },
          null,
          coach.name || 'Treneris',
          'TenisoUžsiėmimai.lt'
        );
      }
      setEmailSuccessOpen(true);
      closeEmailDialog();
      setEmailSelectedUids(new Set());
    } catch (err) {
      console.error(err);
      setEmailError(err?.message || 'Nepavyko išsiųsti el. laiškų.');
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser || !isUserAdmin(currentUser)) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik administratorius gali peržiūrėti šį puslapį.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Treneriai
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 1. Active coaches */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Aktyvūs treneriai
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={openEmailDialog}
              disabled={activeCoaches.length === 0}
            >
              Siųsti el. laišką
            </Button>
            {emailSelectedUids.size > 0 && (
              <Typography variant="body2" color="text.secondary">
                Pasirinkta: {emailSelectedUids.size}
              </Typography>
            )}
          </Box>
        </Box>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={emailSelectedUids.size > 0 && emailSelectedUids.size < activeCoaches.length}
                    checked={activeCoaches.length > 0 && emailSelectedUids.size === activeCoaches.length}
                    onChange={(e) => (e.target.checked ? selectAllCoachesForEmail() : deselectAllCoachesForEmail())}
                    title={activeCoaches.length > 0 ? 'Pasirinkti visus' : ''}
                  />
                </TableCell>
                <TableCell width={48} />
                <TableCell>Vardas</TableCell>
                <TableCell>El. paštas</TableCell>
                <TableCell>Telefonas</TableCell>
                <TableCell>Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedActiveCoaches.map((coach) => {
                const expandKey = coach.reg?.id ?? `active-${coach.uid}`;
                const exp = expandedId === expandKey;
                const isEditing = editing.userId === coach.uid;
                const hasEmail = (coach.email || '').trim().length > 0;
                const isActive = coachActiveStatus[coach.uid];
                return (
                  <React.Fragment key={coach.uid}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={emailSelectedUids.has(coach.uid)}
                          disabled={!hasEmail}
                          onChange={() => toggleEmailSelect(coach.uid)}
                          title={hasEmail ? 'Siųsti el. laišką šiam treneriui' : 'Nėra el. pašto'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpandedId(exp ? null : expandKey)}>
                          {exp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {isEditing ? null : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <span>{coach.name}</span>
                            {isActive === true && <Chip label="Active" size="small" color="success" sx={{ height: 22 }} />}
                            {isActive === false && <Chip label="Not active" size="small" variant="outlined" sx={{ height: 22 }} />}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{isEditing ? null : coach.email}</TableCell>
                      <TableCell>{isEditing ? null : coach.phone}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => startEdit(coach)}>
                          Redaguoti
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                        <Collapse in={exp} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
                                <TextField fullWidth label="Vardas ir pavardė" value={editing.fullName} onChange={(e) => setEditing((x) => ({ ...x, fullName: e.target.value }))} />
                                <TextField fullWidth type="email" label="El. paštas" value={editing.email} onChange={(e) => setEditing((x) => ({ ...x, email: e.target.value }))} />
                                <TextField fullWidth label="Telefonas" value={editing.phoneNumber} onChange={(e) => setEditing((x) => ({ ...x, phoneNumber: e.target.value }))} />
                                <TextField fullWidth multiline minRows={3} label="Aprašymas" value={editing.description} onChange={(e) => setEditing((x) => ({ ...x, description: e.target.value }))} />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <TextField type="number" label="1 asm. (€/val)" value={editing.price1Person} onChange={(e) => setEditing((x) => ({ ...x, price1Person: e.target.value }))} sx={{ width: 120 }} />
                                  <TextField type="number" label="2 asm. (€/val)" value={editing.price2People} onChange={(e) => setEditing((x) => ({ ...x, price2People: e.target.value }))} sx={{ width: 120 }} />
                                  <TextField type="number" label="3 asm. (€/val)" value={editing.price3People} onChange={(e) => setEditing((x) => ({ ...x, price3People: e.target.value }))} sx={{ width: 120 }} />
                                  <TextField type="number" label="4 asm. (€/val)" value={editing.price4People} onChange={(e) => setEditing((x) => ({ ...x, price4People: e.target.value }))} sx={{ width: 120 }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={savingId !== null}>Išsaugoti</Button>
                                  <Button onClick={() => { setEditing({}); setExpandedId(null); }}>Atšaukti</Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar src={coach.cprof?.photoURL || coach.prof?.photoURL} sx={{ width: 64, height: 64 }}>
                                    {coach.name?.[0] || '?'}
                                  </Avatar>
                                  <Typography variant="body2" color="text.secondary">UID: <code style={{ fontSize: '0.75rem' }}>{coach.uid}</code></Typography>
                                </Box>
                                {(coach.email || coach.phone) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Kontaktai:</Typography>
                                    <Typography variant="body2">{coach.email || '—'} · {coach.phone || '—'}</Typography>
                                  </Box>
                                )}
                                {coach.cprof?.description && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Aprašymas:</Typography>
                                    <Typography variant="body2">{coach.cprof.description}</Typography>
                                  </Box>
                                )}
                                {(coach.cprof?.price1Person != null || coach.cprof?.price2People != null) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Kainos (€/val):</Typography>
                                    <Typography variant="body2">1 asm: {coach.cprof.price1Person ?? '—'}, 2 asm: {coach.cprof.price2People ?? '—'}, 3 asm: {coach.cprof.price3People ?? '—'}, 4 asm: {coach.cprof.price4People ?? '—'}</Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {activeCoaches.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Aktyvių trenerių nėra.</Typography>
        )}

        {/* 2. Pending for approval */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Laukia patvirtinimo
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>Vardas</TableCell>
                <TableCell>El. paštas</TableCell>
                <TableCell>Telefonas</TableCell>
                <TableCell>Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingRegistrations.map((reg) => {
                const exp = expandedId === reg.id;
                const prof = getProfile(reg.userId);
                const cprof = getCoachProf(reg.userId);
                const isEditing = editing.userId === reg.userId;
                return (
                  <React.Fragment key={reg.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpandedId(exp ? null : reg.id)}>
                          {exp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{isEditing ? null : (reg.fullName || prof?.fullName || '—')}</TableCell>
                      <TableCell>{isEditing ? null : (reg.email || prof?.email || '—')}</TableCell>
                      <TableCell>{isEditing ? null : (reg.phoneNumber || prof?.phoneNumber || '—')}</TableCell>
                      <TableCell>
                        <Button size="small" variant="contained" color="success" startIcon={approvingId === reg.userId ? <CircularProgress size={16} /> : <ApproveIcon />} disabled={approvingId !== null} onClick={() => handleApprove(reg)} sx={{ mr: 1 }}>
                          Patvirtinti
                        </Button>
                        <Button size="small" onClick={() => startEdit(reg)}>Redaguoti</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, borderBottom: 0 }}>
                        <Collapse in={exp} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, bgcolor: 'grey.50' }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
                                <TextField fullWidth label="Vardas ir pavardė" value={editing.fullName} onChange={(e) => setEditing((x) => ({ ...x, fullName: e.target.value }))} />
                                <TextField fullWidth type="email" label="El. paštas" value={editing.email} onChange={(e) => setEditing((x) => ({ ...x, email: e.target.value }))} />
                                <TextField fullWidth label="Telefonas" value={editing.phoneNumber} onChange={(e) => setEditing((x) => ({ ...x, phoneNumber: e.target.value }))} />
                                <TextField fullWidth multiline minRows={3} label="Aprašymas" value={editing.description} onChange={(e) => setEditing((x) => ({ ...x, description: e.target.value }))} />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  <TextField type="number" label="1 asm. (€/val)" value={editing.price1Person} onChange={(e) => setEditing((x) => ({ ...x, price1Person: e.target.value }))} sx={{ width: 120 }} />
                                  <TextField type="number" label="2 asm. (€/val)" value={editing.price2People} onChange={(e) => setEditing((x) => ({ ...x, price2People: e.target.value }))} sx={{ width: 120 }} />
                                  <TextField type="number" label="3 asm. (€/val)" value={editing.price3People} onChange={(e) => setEditing((x) => ({ ...x, price3People: e.target.value }))} sx={{ width: 120 }} />
                                  <TextField type="number" label="4 asm. (€/val)" value={editing.price4People} onChange={(e) => setEditing((x) => ({ ...x, price4People: e.target.value }))} sx={{ width: 120 }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={savingId !== null}>Išsaugoti</Button>
                                  <Button onClick={() => { setEditing({}); setExpandedId(null); }}>Atšaukti</Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar src={cprof?.photoURL || prof?.photoURL} sx={{ width: 64, height: 64 }}>{(reg.fullName || prof?.fullName)?.[0] || '?'}</Avatar>
                                  <Typography variant="body2" color="text.secondary">UID: <code style={{ fontSize: '0.75rem' }}>{reg.userId}</code></Typography>
                                </Box>
                                {cprof?.description && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Aprašymas:</Typography>
                                    <Typography variant="body2">{cprof.description}</Typography>
                                  </Box>
                                )}
                                {(cprof?.price1Person != null || reg.price1Person != null) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Kainos (€/val):</Typography>
                                    <Typography variant="body2">1 asm: {cprof?.price1Person ?? reg?.price1Person ?? '—'}, 2 asm: {cprof?.price2People ?? reg?.price2People ?? '—'}, 3 asm: {cprof?.price3People ?? reg?.price3People ?? '—'}, 4 asm: {cprof?.price4People ?? reg?.price4People ?? '—'}</Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {pendingRegistrations.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nėra laukiančių patvirtinimo.</Typography>
        )}

        {/* 3. Norintys */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Norintys
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Treneriai, pateikę kontaktus per coaches invite formą. Kiekvienam galite išsiųsti registracijos nuorodą.
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vardas</TableCell>
                <TableCell>El. paštas</TableCell>
                <TableCell>Telefonas</TableCell>
                <TableCell>Veiksmai</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coachInvites.map((invite) => (
                <TableRow key={invite.id}>
                  <TableCell>
                    {[invite.firstName, invite.lastName].filter(Boolean).join(' ') || '—'}
                  </TableCell>
                  <TableCell>{invite.email || '—'}</TableCell>
                  <TableCell>{invite.phone || '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={sendingInviteId === invite.id ? <CircularProgress size={16} /> : <SendIcon />}
                      disabled={sendingInviteId !== null || deletingInviteId !== null || !invite.email}
                      onClick={() => handleSendInviteLink(invite)}
                      sx={{ mr: 1 }}
                    >
                      Siųsti nuorodą
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={deletingInviteId === invite.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                      disabled={sendingInviteId !== null || deletingInviteId !== null}
                      onClick={() => handleDeleteInvite(invite)}
                    >
                      Pašalinti
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {coachInvites.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Norinčių prisijungti dar nėra.
          </Typography>
        )}
      </Container>

      <Dialog open={emailDialogOpen} onClose={closeEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Siųsti el. laišką treneriams</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Gautojai: {activeCoaches.filter((c) => emailSelectedUids.has(c.uid) && (c.email || '').trim()).map((c) => c.name || c.email).join(', ') || '—'}
          </Typography>
          {emailError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailError('')}>
              {emailError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Tema"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="El. laiško tema"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Žinutė"
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            onKeyDown={handleEmailBodyKeyDown}
            inputRef={emailBodyInputRef}
            placeholder="Įrašykite žinutę... (Tab – įtraukti įtrauką)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEmailDialog} disabled={emailSending}>
            Atšaukti
          </Button>
          <Button
            variant="contained"
            startIcon={emailSending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSendCoachEmails}
            disabled={emailSending}
          >
            {emailSending ? 'Siunčiama...' : 'Siųsti'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={inviteSentOpen}
        autoHideDuration={4000}
        onClose={() => setInviteSentOpen(false)}
        message="Registracijos nuoroda išsiųsta el. paštu"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, sm: 24 } }}
      />
      <Snackbar
        open={saveSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setSaveSuccessOpen(false)}
        message="Profilis išsaugotas"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, sm: 24 }, '& .MuiSnackbarContent-root': { bgcolor: 'success.main' } }}
      />
      <Snackbar
        open={emailSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setEmailSuccessOpen(false)}
        message="El. laiškai išsiųsti"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80, sm: 24 }, '& .MuiSnackbarContent-root': { bgcolor: 'success.main' } }}
      />
    </Box>
  );
};

export default AdminTreneriaiPage;
